export type StatusFilter = "all" | "enabled" | "disabled";

export interface ScoringRuleFilters {
  search: string;
  ruleType: string;
  status: StatusFilter;
  clientStatus: StatusFilter;
  minScore: string;
  maxScore: string;
}

// The fields the filters look at. Both the global and per-client scoring rule
// rows are mapped onto this shape so they can share the same filtering.
export interface FilterableScoringRule {
  id: number;
  ruleType: string;
  description: string;
  conciseDescription: string;
  score: number;
  enabled: boolean;
  clientDisabled?: boolean;
}

export const EMPTY_SCORING_RULE_FILTERS: ScoringRuleFilters = {
  search: "",
  ruleType: "",
  status: "all",
  clientStatus: "all",
  minScore: "",
  maxScore: "",
};

export const hasActiveFilters = (filters: ScoringRuleFilters): boolean =>
  (Object.keys(filters) as (keyof ScoringRuleFilters)[]).some(
    (key) => filters[key] !== EMPTY_SCORING_RULE_FILTERS[key],
  );

const parseScore = (value: string): number | null => {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const matchesStatus = (enabled: boolean, status: StatusFilter): boolean =>
  status === "all" || (status === "enabled") === enabled;

export const getRuleTypes = (rules: FilterableScoringRule[]): string[] =>
  Array.from(new Set(rules.map((rule) => rule.ruleType).filter(Boolean))).sort(
    (a, b) => a.localeCompare(b),
  );

export const filterScoringRules = <T>(
  rows: T[],
  filters: ScoringRuleFilters,
  toFilterable: (row: T) => FilterableScoringRule,
): T[] => {
  const search = filters.search.trim().toLowerCase();
  const minScore = parseScore(filters.minScore);
  const maxScore = parseScore(filters.maxScore);

  return rows.filter((row) => {
    const rule = toFilterable(row);

    if (
      search &&
      ![
        String(rule.id),
        rule.ruleType,
        rule.description,
        rule.conciseDescription,
      ].some((value) => value?.toLowerCase().includes(search))
    ) {
      return false;
    }

    if (filters.ruleType && rule.ruleType !== filters.ruleType) return false;
    if (!matchesStatus(rule.enabled, filters.status)) return false;
    if (
      rule.clientDisabled !== undefined &&
      !matchesStatus(!rule.clientDisabled, filters.clientStatus)
    ) {
      return false;
    }
    if (minScore !== null && rule.score < minScore) return false;
    if (maxScore !== null && rule.score > maxScore) return false;

    return true;
  });
};
